/**
 * One conversation, wherever it is being shown.
 *
 * The rail and the full-page agent mode are two frames around the same thing.
 * Duplicating the send/stream/restore logic in both would guarantee they drift
 * — one would get file uploads, the other would keep an old error string — so
 * all of it lives here and the components only decide what it looks like.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY NDJSON AND NOT EventSource
 *
 * `EventSource` is GET-only and cannot carry a body, and a turn here carries a
 * message plus up to four uploaded files. So the request is an ordinary POST
 * and the response is read a line at a time from `body.getReader()`. No
 * library, no second endpoint shape, and file upload keeps working.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api, ApiError, API_BASE, freshAccessToken } from '@/lib/api';
import { getAccess } from '@/lib/auth';
import { useAuth } from '@/contexts/AuthContext';
import { useI18n } from '@/contexts/I18nContext';
import type {
  AgentMessage,
  ConversationResponse,
  PendingChange,
  StreamEvent,
  TraceStep,
} from './types';

export const MAX_FILES = 4;
export const MAX_FILE_BYTES = 10 * 1024 * 1024;
export const ACCEPT = '.xlsx,.xls,.csv,.pdf,.md,.txt,.json';

/**
 * Which thread this browser was last in, per workspace.
 *
 * Only the ID is stored. The transcript itself is re-read from the server, so
 * the panel cannot show a conversation the account has since lost access to,
 * and a stale local copy can never disagree with the ledger it described.
 *
 * Keyed BY ORGANIZATION on purpose. One account holds several companies, and
 * carrying one company's thread into another would show somebody a
 * conversation about a ledger they are no longer looking at.
 */
const THREAD_KEY = 'hipilot_agent_thread';

export function readThread(orgId: string | undefined): string | null {
  if (!orgId) return null;
  try {
    const raw = localStorage.getItem(THREAD_KEY);
    return raw ? ((JSON.parse(raw) as Record<string, string>)[orgId] ?? null) : null;
  } catch {
    return null;
  }
}

export function writeThread(orgId: string | undefined, id: string | null) {
  if (!orgId) return;
  try {
    const raw = localStorage.getItem(THREAD_KEY);
    const map = raw ? (JSON.parse(raw) as Record<string, string>) : {};
    if (id) map[orgId] = id;
    else delete map[orgId];
    localStorage.setItem(THREAD_KEY, JSON.stringify(map));
  } catch {
    /* private browsing, quota — losing the pointer is not worth an error */
  }
}

export function useAgentChat() {
  const { t } = useI18n();
  const { org } = useAuth();
  const qc = useQueryClient();

  const [messages, setMessages] = useState<AgentMessage[]>([]);
  const [conversationId, setConversationId] = useState<string | null>(() => readThread(org?.id));
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sessionCredits, setSessionCredits] = useState(0);
  const [files, setFiles] = useState<File[]>([]);
  const [decided, setDecided] = useState<Record<string, 'applied' | 'discarded' | 'busy'>>({});
  const [loadingThread, setLoadingThread] = useState(false);
  /**
   * The tier, and whether to think. Remembered for the session but not the
   * account: 'auto' is right for almost everyone almost always, and a tier
   * somebody picked once for one hard question should not silently keep
   * charging them ten times over for the next month of small ones.
   */
  const [model, setModel] = useState<string>('auto');
  const [thinking, setThinking] = useState(false);

  /** Lets Stop actually stop — see `stop()`. */
  const abortRef = useRef<AbortController | null>(null);

  // Keep the pointer in step with the thread we are actually in.
  useEffect(() => {
    writeThread(org?.id, conversationId);
  }, [org?.id, conversationId]);

  const openConversation = useCallback(
    async (id: string | null) => {
      if (!id) {
        setMessages([]);
        setConversationId(null);
        setSessionCredits(0);
        setDecided({});
        setError(null);
        return;
      }
      setLoadingThread(true);
      try {
        const res = await api<ConversationResponse>(`/agent/conversations/${id}`);
        const proposals = res.pending ?? [];
        /* Only the undecided ones still need buttons. The rest are history and
           render settled — see the `decided` seeding below. */
        const staged = proposals.filter((p) => !p.decided && !p.expired);
        setConversationId(res.conversation.id);
        setSessionCredits(res.conversation.credits_used);
        setError(null);
        /*
          Seed what was already decided, so a reload shows "Applied" under the
          change it belongs to instead of an Apply button for something that
          has already been posted.
        */
        setDecided(
          Object.fromEntries(
            proposals.filter((p) => p.decided).map((p) => [p.id, p.decided as 'applied' | 'discarded']),
          ),
        );
        setMessages(
          res.messages.map((m, i) => ({
            id: m.id,
            role: m.role,
            text: m.content,
            credits: m.role === 'assistant' && m.credits > 0 ? m.credits : undefined,
            files: (m.attachments ?? []).map((a) => a.filename),
            unread: (m.attachments ?? []).filter((a) => !a.read).map((a) => a.filename),
            trace: m.trace,
            /* The PDFs this reply produced, so a refresh does not empty the
               conversation of the documents it is about. */
            documents: m.documents ?? undefined,
            /*
              Every still-open proposal is hung on the LAST message. The server
              knows which thread staged them but not which reply mentioned them,
              and guessing would put an Apply button under the wrong sentence —
              at the bottom it is at least where the conversation left off.
            */
            pending: i === res.messages.length - 1 ? staged : undefined,
          })),
        );
      } catch {
        writeThread(org?.id, null);
        setConversationId(null);
        setMessages([]);
      } finally {
        setLoadingThread(false);
      }
    },
    [org?.id],
  );

  /**
   * Put the conversation back after a reload.
   *
   * Runs when the workspace resolves, not on mount — `org` arrives with
   * `/auth/me`, and firing before it does would read the pointer for
   * `undefined` and restore nothing.
   */
  useEffect(() => {
    const id = readThread(org?.id);
    if (org?.id && id) void openConversation(id);
    // Only when the workspace changes. `openConversation` is stable per org.
  }, [org?.id, openConversation]);

  const addFiles = useCallback(
    (picked: FileList | File[] | null) => {
      if (!picked) return;
      const next: File[] = [];
      for (const f of Array.from(picked)) {
        // Rejected here as well as on the server, so somebody who drops a 60 MB
        // export finds out immediately instead of after uploading it.
        if (f.size > MAX_FILE_BYTES) {
          setError(t('agent_file_too_big').replace('{name}', f.name));
          continue;
        }
        next.push(f);
      }
      setFiles((cur) => [...cur, ...next].slice(0, MAX_FILES));
    },
    [t],
  );

  const removeFile = useCallback((index: number) => {
    setFiles((cur) => cur.filter((_, i) => i !== index));
  }, []);

  const stop = useCallback(() => {
    /*
      Aborts the RESPONSE, not the work. The server deliberately finishes the
      turn it already paid for and writes it to the transcript — so the answer
      is in the thread on the next load, rather than the tenant having been
      billed for tokens that went nowhere.
    */
    abortRef.current?.abort();
    abortRef.current = null;
    setPending(false);
  }, []);

  const send = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      const attached = files;
      if ((!trimmed && attached.length === 0) || pending) return;

      const assistantId = crypto.randomUUID();
      setMessages((m) => [
        ...m,
        {
          id: crypto.randomUUID(),
          role: 'user',
          text: trimmed,
          files: attached.map((f) => f.name),
        },
        { id: assistantId, role: 'assistant', text: '', trace: [], streaming: true },
      ]);
      setFiles([]);
      setPending(true);
      setError(null);

      /** Update the assistant message being written, without touching the rest. */
      const patch = (fn: (m: AgentMessage) => AgentMessage) =>
        setMessages((cur) => cur.map((m) => (m.id === assistantId ? fn(m) : m)));

      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const body = new FormData();
        body.append('message', trimmed || t('agent_files'));
        if (conversationId) body.append('conversation_id', conversationId);
        for (const f of attached) body.append('files', f, f.name);

        /*
          Raw fetch rather than the shared `api()` helper: this response is a
          stream, and `api()` reads the whole body as JSON before returning —
          which would defeat the entire point.

          But bypassing `api()` also bypassed its refresh-on-401, and access
          tokens live fifteen minutes. Anybody working longer than that
          eventually sent a message on a dead token and got "Something went
          wrong. Try again." — which failed identically on every retry, because
          the expiry was not what "try again" addressed.

          So the token is refreshed BEFORE the request rather than after the
          failure: a stream that dies halfway has already shown the customer
          half an answer, and there is no clean way to resume it.
        */
        const token = await freshAccessToken();
        const res = await fetch(`${API_BASE}/agent/chat/stream`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token ?? getAccess() ?? ''}` },
          body,
          signal: controller.signal,
        });

        if (!res.ok || !res.body) {
          let code = 'AGENT_FAILED';
          try {
            code = ((await res.json()) as { code?: string }).code ?? code;
          } catch {
            /* a non-JSON error body is still a failure, just an unlabelled one */
          }
          throw new ApiError(res.status, code, 'agent failed');
        }

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let streamedText = '';
        let streamedThinking = '';

        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });

          /*
            Split on newlines and KEEP THE REMAINDER. A chunk boundary can land
            in the middle of a JSON object, and parsing that half would throw on
            perfectly good data. The tail stays in the buffer until its newline
            arrives.
          */
          const lines = buffer.split('\n');
          buffer = lines.pop() ?? '';

          for (const line of lines) {
            if (!line.trim()) continue;
            let ev: StreamEvent;
            try {
              ev = JSON.parse(line) as StreamEvent;
            } catch {
              continue;
            }

            if (ev.type === 'delta') {
              streamedText += ev.text;
              patch((m) => ({ ...m, text: streamedText }));
            } else if (ev.type === 'thinking') {
              streamedThinking += ev.text;
              patch((m) => ({ ...m, thinking: streamedThinking }));
            } else if (ev.type === 'model') {
              /* Which tier took the turn, recorded on the message so the
                 answer and its price stay attached to each other. */
              patch((m) => ({
                ...m,
                tier: { id: ev.id, name: ev.name, reason: ev.reason, thinking: ev.thinking },
              }));
            } else if (ev.type === 'tool_start') {
              const step: TraceStep = {
                id: ev.id,
                name: ev.name,
                label: ev.label,
                running: true,
                afterChars: streamedText.length,
              };
              patch((m) => ({ ...m, trace: [...(m.trace ?? []), step] }));
            } else if (ev.type === 'tool_end') {
              patch((m) => ({
                ...m,
                trace: (m.trace ?? []).map((s) =>
                  s.id === ev.id ? { ...s, running: false, ok: ev.ok, summary: ev.summary } : s,
                ),
              }));
            } else if (ev.type === 'step') {
              patch((m) => ({ ...m, credits: ev.totalCredits }));
            } else if (ev.type === 'document') {
              patch((m) => ({ ...m, documents: [...(m.documents ?? []), ev.doc] }));
            } else if (ev.type === 'staged') {
              patch((m) => ({ ...m, pending: [...(m.pending ?? []), ev.change] }));
            } else if (ev.type === 'attachments') {
              const unread = ev.files.filter((f) => !f.read).map((f) => f.filename);
              if (unread.length) patch((m) => ({ ...m, unread }));
            } else if (ev.type === 'error') {
              throw new ApiError(500, ev.code, ev.message);
            } else if (ev.type === 'end') {
              setConversationId(ev.conversation_id);
              setSessionCredits((c) => c + ev.credits_used);
              patch((m) => ({
                ...m,
                /*
                  `reply` wins over the accumulated deltas. They are normally
                  identical, but when the loop stops early — out of credits, out
                  of steps — the server substitutes an explanation that was
                  never streamed, and that explanation is the one thing the
                  customer most needs to read.
                */
                text: ev.reply || m.text,
                credits: ev.credits_used,
                pending: ev.pending?.length ? ev.pending : m.pending,
                streaming: false,
              }));
              /*
                The rail's meter (layout/SidebarCredits.tsx) reads the
                `['billing']` query. Spending credits makes that cache wrong,
                and a meter that only refreshes on navigation is exactly the
                meter that reads "18,000 left" while the agent is saying it has
                run out.
              */
              if (ev.credits_used > 0) void qc.invalidateQueries({ queryKey: ['billing'] });
              void qc.invalidateQueries({ queryKey: ['agent-conversations'] });
            }
          }
        }
        patch((m) => ({ ...m, streaming: false }));
      } catch (e) {
        if (e instanceof DOMException && e.name === 'AbortError') {
          patch((m) => ({ ...m, streaming: false }));
        } else {
          const code = e instanceof ApiError ? e.code : null;
          /*
            Running out of credits is not an error, it is a purchase decision
            arriving at an awkward moment. Marked with a sentinel the message
            list can recognise, so it renders a card with a button rather than a
            red line the reader can do nothing about.
          */
          setError(code === 'NO_CREDITS' ? 'NO_CREDITS' : t('agent_failed'));
          // Drop the empty assistant bubble — an error message below an empty
          // reply reads as two failures instead of one.
          setMessages((cur) => cur.filter((m) => !(m.id === assistantId && !m.text)));
        }
      } finally {
        abortRef.current = null;
        setPending(false);
      }
    },
    [conversationId, files, model, pending, qc, t, thinking],
  );

  const decide = useCallback(
    async (id: string, action: 'apply' | 'discard') => {
      setDecided((d) => ({ ...d, [id]: 'busy' }));
      try {
        const res = await api<{ note?: string }>(`/agent/changes/${id}/${action}`, {
          method: 'POST',
        });
        setDecided((d) => ({ ...d, [id]: action === 'apply' ? 'applied' : 'discarded' }));
        /* The server writes this into the transcript; show it immediately
           rather than making the customer reload to learn what happened. */
        if (action === 'apply' && res?.note) {
          setMessages((m) => [
            ...m,
            { id: `applied-${id}`, role: 'assistant', text: res.note as string },
          ]);
        }
        /*
          An applied change writes to the ledger, so every list the app holds is
          now stale. Clearing the whole cache is blunt and correct: the agent can
          post an expense, an invoice or a payment, and each of those moves
          several unrelated screens — the dashboard, the account, the customer.
        */
        if (action === 'apply') void qc.invalidateQueries();
      } catch {
        setDecided((d) => {
          const next = { ...d };
          delete next[id];
          return next;
        });
        setError(t('agent_apply_failed'));
      }
    },
    [qc, t],
  );

  const newConversation = useCallback(() => {
    void openConversation(null);
    writeThread(org?.id, null);
    setFiles([]);
  }, [openConversation, org?.id]);

  return {
    messages,
    conversationId,
    model,
    setModel,
    thinking,
    setThinking,
    pending,
    error,
    setError,
    sessionCredits,
    files,
    addFiles,
    removeFile,
    decided,
    decide,
    send,
    stop,
    newConversation,
    openConversation,
    loadingThread,
  };
}

export type AgentChat = ReturnType<typeof useAgentChat>;

/** Re-exported so components import one module, not three. */
export type { AgentMessage, PendingChange, TraceStep };
